import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import InputMask from 'react-input-mask';
import { useFormik } from 'formik';
import { Link } from 'react-router-dom';
import api from 'service/api';
import useStyles from '../styles';
import {
  initialValues,
  genreOptions,
  civilStatusOptions,
  STEP_LABELS,
} from '../initialValues';
import { stepSchemas } from '../schema';

const cleanDigits = (value = '') => String(value).replace(/\D/g, '');

function DoctorRegisterForm() {
  const classes = useStyles();
  const formScrollRef = useRef(null);
  const [step, setStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [states, setStates] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [resolvedCep, setResolvedCep] = useState('');

  const formik = useFormik({
    initialValues,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: () => {},
  });

  useEffect(() => {
    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const [statesRes, specialtiesRes] = await Promise.all([
          api.get('/v1/states', { page: 1, per_page: 50 }),
          api.get('/v1/specialties', { page: 1, per_page: 100 }),
        ]);
        if (statesRes.ok) setStates(statesRes.data?.data || []);
        if (specialtiesRes.ok) setSpecialties(specialtiesRes.data?.data || []);
      } catch (e) {
        // options optional until step 3
      } finally {
        setLoadingOptions(false);
      }
    };
    loadOptions();
  }, []);

  const setAddressField = (name, value) => {
    formik.setFieldValue(name, value);
    formik.setFieldError(name, undefined);
  };

  const clearResolvedAddress = () => {
    setAddressField('address.city.id', null);
    setAddressField('address.city.name', '');
    setAddressField('address.city.state.id', null);
    setAddressField('address.city.state.name', '');
    setAddressField('address.city.state.uc', '');
    setAddressField('address.street', '');
    setAddressField('address.district', '');
  };

  useEffect(() => {
    const cep = cleanDigits(formik.values.address.code_post);
    if (cep === resolvedCep) return undefined;

    if (resolvedCep) {
      clearResolvedAddress();
      setResolvedCep('');
    }

    if (cep.length < 8) return undefined;

    const timeout = setTimeout(async () => {
      try {
        setLoadingCep(true);
        setFormError('');
        const res = await api.get(`/v2/public/code_post/${cep}`);
        if (!res.ok) throw res.data;
        const address = res.data?.data;
        setAddressField('address.city.id', address?.city?.id || null);
        setAddressField('address.city.name', address?.city?.name || '');
        setAddressField(
          'address.city.state.id',
          address?.city?.state?.id || null
        );
        setAddressField(
          'address.city.state.name',
          address?.city?.state?.name || ''
        );
        setAddressField(
          'address.city.state.uc',
          address?.city?.state?.uc || ''
        );
        setAddressField('address.street', address?.street || '');
        setAddressField('address.district', address?.district || '');
        setResolvedCep(cep);
      } catch (err) {
        clearResolvedAddress();
        setFormError(err?.msg || 'CEP não encontrado');
      } finally {
        setLoadingCep(false);
      }
    }, 700);

    return () => clearTimeout(timeout);
  }, [formik.values.address.code_post, resolvedCep]);

  const scrollToFirstError = () => {
    const container = formScrollRef.current;
    if (!container) return;
    const target = container.querySelector('.Mui-error');
    if (target) {
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    container.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const setValue = (name, value) => {
    formik.setFieldValue(name, value);
    formik.setFieldError(name, undefined);
    setFormError('');
  };

  const validateCurrentStep = async () => {
    const schema = stepSchemas[step];
    try {
      await schema.validate(formik.values, { abortEarly: false });
      formik.setErrors({});
      return true;
    } catch (err) {
      const errors = {};
      err.inner?.forEach((e) => {
        if (e.path) errors[e.path] = e.message;
      });
      // Formik nested errors need object shape for helpers
      const nested = {};
      Object.keys(errors).forEach((path) => {
        const parts = path.split('.');
        let cursor = nested;
        parts.forEach((part, idx) => {
          if (idx === parts.length - 1) cursor[part] = errors[path];
          else {
            cursor[part] = cursor[part] || {};
            cursor = cursor[part];
          }
        });
      });
      formik.setErrors(nested);
      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => {
          const parts = key.split('.');
          let cursor = acc;
          parts.forEach((part, idx) => {
            if (idx === parts.length - 1) cursor[part] = true;
            else {
              cursor[part] = cursor[part] || {};
              cursor = cursor[part];
            }
          });
          return acc;
        }, {}),
        false
      );
      return false;
    }
  };

  const setFieldValidationError = (field, message) => {
    formik.setFieldTouched(field, true, false);
    formik.setFieldError(field, message);
    setFormError(message);
    setTimeout(scrollToFirstError, 0);
  };

  const checkCpfAvailable = async () => {
    const cpf = cleanDigits(formik.values.cpf);
    if (!cpf) return false;
    try {
      const res = await api.get('/v1/users/check_cpf_public', { cpf });
      const exists = res?.data?.data?.exists;
      if (res.ok && exists) {
        setFieldValidationError(
          'cpf',
          res.data?.msg || 'Este CPF já possui uma conta cadastrada.'
        );
        return false;
      }
    } catch (e) {
      setFieldValidationError(
        'cpf',
        'Não foi possível validar o CPF. Tente novamente.'
      );
      return false;
    }
    return true;
  };

  const checkEmailAvailable = async () => {
    const email = (formik.values.email || '').trim();
    if (!email) return false;
    try {
      const res = await api.get('/v1/users/check_email_public', { email });
      const exists = res?.data?.data?.exists;
      if (res.ok && exists) {
        setFieldValidationError(
          'email',
          res.data?.msg || 'Este e-mail já possui uma conta cadastrada.'
        );
        return false;
      }
    } catch (e) {
      setFieldValidationError(
        'email',
        'Não foi possível validar o e-mail. Tente novamente.'
      );
      return false;
    }
    return true;
  };

  const buildPayload = () => {
    const v = formik.values;
    return {
      name: v.name.trim(),
      email: v.email.trim(),
      cpf: cleanDigits(v.cpf),
      cell_phone: cleanDigits(v.cell_phone),
      birth_date: v.birth_date,
      password: v.password,
      genre: v.genre || undefined,
      civil_status: v.civil_status || undefined,
      nationality: { id: 1 },
      group: { id: 3 },
      specialties: (v.specialties || []).map((s) => ({ id: s.id })),
      crm: {
        number: v.crm.number,
        state: { id: v.crm.state.id },
      },
      address: {
        code_post: cleanDigits(v.address.code_post),
        street: v.address.street,
        number: v.address.number,
        district: v.address.district,
        complement: v.address.complement || undefined,
        city: { id: v.address.city.id },
      },
    };
  };

  const handleNext = async () => {
    setFormError('');
    setSuccessMsg('');
    const ok = await validateCurrentStep();
    if (!ok) {
      setTimeout(scrollToFirstError, 0);
      return;
    }

    if (step === 0) {
      const cpfOk = await checkCpfAvailable();
      if (!cpfOk) return;
      const emailOk = await checkEmailAvailable();
      if (!emailOk) return;
    }

    if (step < 2) {
      setStep((s) => s + 1);
      return;
    }

    setLoadingSubmit(true);
    try {
      const payload = buildPayload();
      const res = await api.post('/v1/users/register', payload);
      if (!res.ok) {
        throw res.data || res;
      }
      setSuccessMsg(
        'Cadastro realizado com sucesso! Acesse sua conta pelo app Timelink, usando seu CPF e a senha que você acabou de cadastrar.'
      );
    } catch (err) {
      setFormError(
        err?.msg ||
          err?.data?.msg ||
          'Não foi possível concluir o cadastro. Tente novamente.'
      );
    } finally {
      setLoadingSubmit(false);
    }
  };

  const getNestedError = (path) => {
    const parts = path.split('.');
    let err = formik.errors;
    let touch = formik.touched;
    for (const part of parts) {
      err = err?.[part];
      touch = touch?.[part];
    }
    return touch && err ? err : '';
  };

  const handleFieldChange = (name) => (event) => {
    formik.handleChange(event);
    formik.setFieldError(name, undefined);
    setFormError('');
  };

  const field = (name, label, extra = {}) => (
    <TextField
      className={classes.text}
      fullWidth
      variant="outlined"
      id={name}
      name={name}
      label={label}
      value={formik.values[name] ?? ''}
      onChange={handleFieldChange(name)}
      onBlur={formik.handleBlur}
      error={Boolean(getNestedError(name))}
      helperText={getNestedError(name)}
      {...extra}
    />
  );

  const maskedField = (name, label, mask, extra = {}) => (
    <InputMask
      mask={mask}
      value={formik.values[name] ?? ''}
      onChange={handleFieldChange(name)}
      onBlur={formik.handleBlur}
      maskChar={null}
    >
      {(inputProps) => (
        <TextField
          {...inputProps}
          className={classes.text}
          fullWidth
          variant="outlined"
          id={name}
          name={name}
          label={label}
          error={Boolean(getNestedError(name))}
          helperText={getNestedError(name)}
          {...extra}
        />
      )}
    </InputMask>
  );

  const addressState = formik.values.address.city.state;
  const addressStateUc =
    addressState.uc ||
    states.find((item) => item.id === addressState.id)?.uc ||
    addressState.name;

  const handleFormSubmit = (event) => {
    event.preventDefault();
    handleNext();
  };

  if (successMsg) {
    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography className={classes.title}>Cadastro concluído</Typography>
        </div>
        <div className={classes.formScroll}>
          <div className={classes.success}>{successMsg}</div>
        </div>
        <div className={classes.actions}>
          <Link to="/login" style={{ textDecoration: 'none' }}>
            <Typography className={classes.link}>
              Voltar para o login
            </Typography>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form className={classes.root} onSubmit={handleFormSubmit} noValidate>
      <div className={classes.header}>
        <Typography className={classes.title}>Cadastro de médico</Typography>
        <Typography className={classes.subtitle}>
          Preencha os dados para criar sua conta no Timelink
        </Typography>

        <div className={classes.stepper}>
          {STEP_LABELS.map((_, idx) => (
            <div
              key={idx}
              className={`${classes.stepDot} ${
                idx <= step ? classes.stepDotActive : ''
              }`}
            />
          ))}
        </div>
        <Typography className={classes.stepLabel}>
          {STEP_LABELS[step]}
        </Typography>
      </div>

      <div className={classes.formScroll} ref={formScrollRef}>
        {step === 0 && (
          <>
            {maskedField('cpf', 'CPF', '999.999.999-99')}
            {field('name', 'Nome completo')}
            {field('email', 'E-mail', { type: 'email' })}
            {maskedField('cell_phone', 'Celular', '(99) 99999-9999')}
            {maskedField('birth_date', 'Data de nascimento', '99/99/9999')}
            <TextField
              className={classes.text}
              select
              fullWidth
              variant="outlined"
              id="genre"
              name="genre"
              label="Sexo (opcional)"
              value={formik.values.genre}
              onChange={formik.handleChange}
            >
              <MenuItem value="">
                <em>Não informar</em>
              </MenuItem>
              {genreOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              className={classes.text}
              select
              fullWidth
              variant="outlined"
              id="civil_status"
              name="civil_status"
              label="Estado civil (opcional)"
              value={formik.values.civil_status}
              onChange={formik.handleChange}
            >
              <MenuItem value="">
                <em>Não informar</em>
              </MenuItem>
              {civilStatusOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              className={classes.text}
              fullWidth
              variant="outlined"
              id="password"
              name="password"
              label="Senha"
              type={showPassword ? 'text' : 'password'}
              value={formik.values.password}
              onChange={handleFieldChange('password')}
              onBlur={formik.handleBlur}
              error={Boolean(getNestedError('password'))}
              helperText={getNestedError('password')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityIcon />
                      ) : (
                        <VisibilityOffIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              className={classes.text}
              fullWidth
              variant="outlined"
              id="password_confirm"
              name="password_confirm"
              label="Confirmar senha"
              type={showPassword ? 'text' : 'password'}
              value={formik.values.password_confirm}
              onChange={handleFieldChange('password_confirm')}
              onBlur={formik.handleBlur}
              error={Boolean(getNestedError('password_confirm'))}
              helperText={getNestedError('password_confirm')}
            />
          </>
        )}

        {step === 1 && (
          <>
            <InputMask
              mask="99999-999"
              value={formik.values.address.code_post}
              onChange={(e) => setValue('address.code_post', e.target.value)}
              onBlur={formik.handleBlur}
              maskChar={null}
            >
              {(inputProps) => (
                <TextField
                  {...inputProps}
                  className={classes.text}
                  fullWidth
                  variant="outlined"
                  id="address.code_post"
                  name="address.code_post"
                  label="CEP"
                  error={Boolean(getNestedError('address.code_post'))}
                  helperText={
                    loadingCep
                      ? 'Buscando CEP...'
                      : getNestedError('address.code_post')
                  }
                />
              )}
            </InputMask>
            <div className={classes.row}>
              <TextField
                className={classes.text}
                fullWidth
                variant="outlined"
                id="address.city.name"
                label="Cidade"
                value={formik.values.address.city.name}
                disabled
                error={Boolean(getNestedError('address.city.name'))}
                helperText={getNestedError('address.city.name')}
              />
              <TextField
                className={classes.text}
                fullWidth
                variant="outlined"
                id="address.city.state.uc"
                label="UF"
                value={addressStateUc}
                disabled
                error={Boolean(getNestedError('address.city.state.name'))}
                helperText={getNestedError('address.city.state.name')}
              />
            </div>
            <TextField
              className={classes.text}
              fullWidth
              variant="outlined"
              id="address.street"
              name="address.street"
              label="Rua"
              value={formik.values.address.street}
              onChange={handleFieldChange('address.street')}
              onBlur={formik.handleBlur}
              error={Boolean(getNestedError('address.street'))}
              helperText={getNestedError('address.street')}
            />
            <div className={classes.row}>
              <TextField
                className={classes.text}
                fullWidth
                variant="outlined"
                id="address.number"
                name="address.number"
                label="Número"
                value={formik.values.address.number}
                onChange={handleFieldChange('address.number')}
                onBlur={formik.handleBlur}
                error={Boolean(getNestedError('address.number'))}
                helperText={getNestedError('address.number')}
              />
              <TextField
                className={classes.text}
                fullWidth
                variant="outlined"
                id="address.district"
                name="address.district"
                label="Bairro"
                value={formik.values.address.district}
                onChange={handleFieldChange('address.district')}
                onBlur={formik.handleBlur}
                error={Boolean(getNestedError('address.district'))}
                helperText={getNestedError('address.district')}
              />
            </div>
            <TextField
              className={classes.text}
              fullWidth
              variant="outlined"
              id="address.complement"
              name="address.complement"
              label="Complemento (opcional)"
              value={formik.values.address.complement}
              onChange={formik.handleChange}
            />
          </>
        )}

        {step === 2 && (
          <>
            <TextField
              className={classes.text}
              fullWidth
              variant="outlined"
              id="crm.number"
              name="crm.number"
              label="Número do CRM"
              value={formik.values.crm.number}
              onChange={handleFieldChange('crm.number')}
              onBlur={formik.handleBlur}
              error={Boolean(getNestedError('crm.number'))}
              helperText={getNestedError('crm.number')}
            />
            <Autocomplete
              className={classes.text}
              options={states}
              loading={loadingOptions}
              getOptionLabel={(opt) =>
                opt?.uc ? `${opt.uc} - ${opt.name}` : opt?.name || ''
              }
              getOptionSelected={(option, value) => option?.id === value?.id}
              value={formik.values.crm.state}
              onChange={(_, value) => setValue('crm.state', value)}
              noOptionsText="Nenhuma opção encontrada"
              loadingText="Carregando..."
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="UF do CRM"
                  error={Boolean(getNestedError('crm.state'))}
                  helperText={getNestedError('crm.state')}
                />
              )}
            />
            <Autocomplete
              className={classes.text}
              multiple
              options={specialties}
              loading={loadingOptions}
              getOptionLabel={(opt) => opt?.name || ''}
              getOptionSelected={(option, value) => option?.id === value?.id}
              value={formik.values.specialties}
              onChange={(_, value) => setValue('specialties', value)}
              noOptionsText="Nenhuma opção encontrada"
              loadingText="Carregando..."
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Especialidades"
                  error={Boolean(getNestedError('specialties'))}
                  helperText={getNestedError('specialties')}
                />
              )}
            />
          </>
        )}
      </div>

      {formError && <div className={classes.error}>{formError}</div>}

      <div className={classes.actions}>
        {step > 0 && (
          <Button
            className={classes.secondary}
            type="button"
            variant="outlined"
            color="primary"
            fullWidth
            onClick={() => {
              setFormError('');
              setStep((s) => s - 1);
            }}
            disabled={loadingSubmit}
          >
            Voltar
          </Button>
        )}
        <Button
          className={classes.submit}
          type="submit"
          variant="contained"
          fullWidth
          disabled={loadingSubmit || loadingCep}
        >
          {loadingSubmit ? (
            <CircularProgress style={{ color: 'white' }} size={24} />
          ) : step === 2 ? (
            'Criar conta'
          ) : (
            'Continuar'
          )}
        </Button>
        <Link to="/login" style={{ textDecoration: 'none' }}>
          <Typography className={classes.link}>
            Já tenho conta — Entrar
          </Typography>
        </Link>
      </div>
    </form>
  );
}

export default DoctorRegisterForm;
